import { get } from 'lodash';

export default ['$scope', '$routeParams', '$location', '$window', 'AccountApplicationV1', 'users', 'Comment', 'Client', 'Agreement', 'StaffAccountApplication', 'PortfolioModels',
  function($scope, $routeParams, $location, $window, AccountApplicationV1, users, Comment, Client, Agreement, StaffAccountApplication, PortfolioModels) {
    StaffAccountApplication.get($routeParams, (application) => {
      this.accountApplication = application.accountApplication;
      this.clientProfile = application.clientProfile;
      this.user = application.user;
      this.rc518Updated = application.rc518Updated;
      this.pmName = `${this.accountApplication.pmFirstName} ${this.accountApplication.pmLastName}`;
      this.employmentIncome = get(this, 'accountApplication.answers.employmentIncome');
      this.investmentIncome = get(this, 'accountApplication.answers.investmentIncome');
      this.otherIncome = get(this, 'accountApplication.answers.otherIncome');
      this.totalIncome = get(this, 'accountApplication.answers.income');
      this.earlyWithdrawal = get(this, 'accountApplication.answers.earlyWithdrawal');
      this.isEarlyWithdrawalSet = typeof this.earlyWithdrawal === 'boolean';
      const incomeToNetWorthRatioValue = this.totalIncome / this.accountApplication.netWorth;
      this.incomeToNetWorthRatio = incomeToNetWorthRatioValue.toFixed(3);
      this.isTemporarySIN = application.clientProfile.taxId && application.clientProfile.taxId[0] === '9';
      this.eidCheckStatus = application.userEidCheckStatus
      this.watchlistCheckStatus = application.userWatchlistCheckStatus
      this.canadianResident = this.clientProfile.canadianResident || get(this, 'accountApplication.answers.homeCountry') === 'CAN';
      const otherCitizenships = get(this, 'accountApplication.answers.otherCitizenships');
      const profileOtherCitizenships = this.clientProfile.otherCitizenships;
      const legacyOtherCitizenships = this.clientProfile.legacyOtherCitizenships;
      const isCanadian = get(this, 'accountApplication.answers.isCanadianCitizen');
      const isUs = get(this, 'accountApplication.answers.isUsCitizen');
      this.otherCitizenships = null;
      // If the clientProfile has their other citizenship defined as an array, use that as the first choice.
      if(profileOtherCitizenships && Array.isArray(profileOtherCitizenships)) {
        this.otherCitizenships = profileOtherCitizenships.join(', ');
      // Otherwise, use the application values, which should always be an array but the check is added as an unlikely edge case.
      } else if(otherCitizenships) {
        this.otherCitizenships = Array.isArray(otherCitizenships) ? otherCitizenships.join(', ') : otherCitizenships;
      // Finally, if none of the above have values, use the legacy citizenship value, which should be a string.
      } else if(legacyOtherCitizenships) {
        this.otherCitizenships = legacyOtherCitizenships;
      }
      this.isCanadianSet = typeof isCanadian === 'boolean';
      this.isUsSet = typeof isUs === 'boolean';
      this.isCanadian = isCanadian;
      this.isUs = isUs;
      // PM Approval Settings
      this.pmApprovalCurrentDate = null;
      this.pmApprovalValue = null;
      this.pmApprovalType = { value: null, name: null, date: null, isSelected: false, datepicker: null };
      this.pmApprovalDatePicker = {
        startDate: new Date((new Date().getFullYear() - 5), (new Date().getMonth()), (new Date().getDate())),
        endDate: new Date(),
        dateSelected: (value) => {
          this.pmApprovalType.date = moment(value).format('YYYY-MM-DD');
          this.pmApprovalCurrentDate = value;
        },
        isSelected: (value, datepicker) => {
          this.pmApprovalError = this.pmApprovalError ? '' : this.pmApprovalError;
          this.pmApprovalType.isSelected = !!value;
          this.pmApprovalType.datepicker = datepicker;
        }
      };
      this.regulatoryQuestionsValues = this.getRegulatoryQuestions();
      this.hasRegulatoryQuestions = this.setHasRegulatoryQuestions();

      const birthday = moment(this.clientProfile.dateOfBirth);
      const dateNow = moment(new Date());
      this.age = dateNow.diff(birthday, 'years');

      if(this.accountApplication.agreementSignatureId) {
        this.agreementURL = this.accountApplication.agreementSignatureId;
      }

      const answers = get(this, 'accountApplication.answers');
      if(answers && answers.investorSuitability != null) {
        this.investorSuitability = Object.keys(answers.investorSuitability)
          .sort()
          .map(key => ({
            question: answers.investorSuitability[key].question,
            value: answers.investorSuitability[key].value,
          }))
          // We're no longer committing 'Recommended Portfolio' to the DB,
          // but we need to filter it out of older applications
          .filter(key => key.question !== 'Recommended Portfolio');
      }

      const roundToThousandth = num => Math.round(num * 1000) / 1000;

      const suitabilityScoreAsFloat = parseFloat(this.accountApplication.suitabilityScore) || 0;
      this.suitabilityScoreRounded = roundToThousandth(suitabilityScoreAsFloat);

      const portfolioModelId = get(this, 'accountApplication.answers.portfolioModelId');
      if(portfolioModelId) {
        PortfolioModels.get(
          { id: portfolioModelId },
          (selectedPortfolio) => {
            this.portfolioRisk = selectedPortfolio.risk;
            this.showDisclosureTriggerInfo = this.getDisclosureTriggerDescriptions(answers);
          },
        );
      }

      this.suitabilityScoreVsPortfolioScore = () => {
        if(this.portfolioRisk !== undefined
          && this.accountApplication.suitabilityScore !== undefined) {
          return this.accountApplication.suitabilityScore >= this.portfolioRisk;
        }

        return false;
      };

      const maritalStatus = get(this, 'accountApplication.answers.maritalStatus');
      if(maritalStatus) {
        this.hasSpouse = ['married', 'common'].includes(maritalStatus);
      }

      this.setConversationTriggerProperties(application);
      this.setPMApproveDisabled();
    });

    this.setConversationTriggerProperties = (application) => {
      this.showConversationRequired = !!(application.accountApplication.submittedAt);
      this.applicationConversationRequired = application.applicationConversationRequired;
      this.currentConversationRequired = application.currentConversationRequired;

      this.actionedConversationTriggers = application.actionedConversationTriggers;
      this.unactionedConversationTriggers = application.unactionedConversationTriggers;

      this.showActionedConversationTriggers = !!(this.showConversationRequired && this.actionedConversationTriggers);
      this.showUnactionedConversationTriggers = !!(this.showConversationRequired && this.unactionedConversationTriggers);

      this.showConversationTriggers = !!(this.showActionedConversationTriggers || this.showUnactionedConversationTriggers)
    };

    this.setPMApproveDisabled = () => {
      this.pmApproveDisabled = !!(this.currentConversationRequired && !this.accountApplication.pmApproval?.approved)
    }

    this.getDisclosureTriggerDescriptions = (answers) => {
      const description = [];

      // we will keep this function in system, since we may need to add disclosure trigger in the future
      return description.sort();
    };

    this.userId = users.getCurrentUser().id;

    this.loadComments = () => AccountApplicationV1.getComments($routeParams).$promise
      .then((comments) => {
        this.accountApplication.comments = comments.map(this.processComment);
      });

    this.statusClass = () => {
      switch(this.accountApplication && this.accountApplication.status) {
        case 'incomplete': return 'text-warning';
        case 'processed': return 'text-highlight';
        default: return 'text-primary';
      }
    };

    this.setState = (state) => {
      return AccountApplicationV1.state({ id: this.accountApplication.id, state })
        .$promise.then(() => {
          this.accountApplication.status = state;
          this.accountApplication.comments = AccountApplicationV1.getComments($routeParams);
          return this.accountApplication.comments;
        });
    };

    this.cancel = () => confirm('Are you sure you want to delete the application?')
      && AccountApplicationV1.delete(
        { id: this.accountApplication.id },
        () => $window.history.back());

    this.saveFintrac = () => {
      delete this.fintracError;
      $scope.fintracForm.$setPristine();
      return Client.update(
        {
          id: this.user.id,
          fintrac: {
            risk: this.clientProfile.fintrac.risk,
            flagged: this.clientProfile.fintrac.flagged,
            reported: this.clientProfile.fintrac.reported,
          },
        }).$promise.catch((e) => {
          this.fintracError = e.data;
        });
    };

    this.saveAccountNumber = () => {
      delete this.accountNumberError;
      $scope.accountNumberForm.$setPristine();
      return AccountApplicationV1.update({
        id: this.accountApplication.id,
        accountNumber: this.accountApplication.accountNumber,
      })
        .$promise.catch((e) => {
          this.accountNumberError = e.data;
        });
    };

    $scope.$watch('ctrl.pmApprovalValue', (value) => {
      if(typeof value !== 'number') return
      this.pmApprovalCurrentDate = null;
      this.pmApprovalType.isSelected = false;
      this.pmApprovalType.datepicker = null;
      const approvalTypeNames = [{ name: 'KYC' }, { name: 'Face to Face' }, { name: 'Phone Call' }, { name: 'Message' }];
      this.pmApprovalType.value = value;
      this.pmApprovalType.name = approvalTypeNames[value].name;
      this.pmApprovalError = this.pmApprovalError ? '' : this.pmApprovalError;
    })

    this.pmApprovalToggle = () => {
      let pmApproved;
      let pmBy;
      let pmDate;
      let pmType;
      let pmOn;
      delete this.pmApprovalError;
      if(this.accountApplication.pmApproval) {
        pmApproved = !this.accountApplication.pmApproval.approved;
      } else {
        pmApproved = true;
      }
      if(pmApproved) {
        if(
          typeof this.pmApprovalType.value === 'number' &&
          (this.pmApprovalType.name === 'KYC' ||
            (this.pmApprovalType.isSelected && this.pmApprovalType.datepicker === this.pmApprovalType.name && this.pmApprovalType.date))
        ) {
          pmType = this.pmApprovalType.name;
          pmOn = this.pmApprovalType.date;
        } else {
          this.pmApprovalError = 'Please provide an approval type, and date, where applicable.'
          return;
        }
        pmDate = new Date();
        pmDate = pmDate.toISOString();
        pmBy = users.getCurrentUser().id;
      } else {
        this.pmApprovalValue = null;
        this.pmApprovalType = { value: null, name: null, date: null, isSelected: false, datepicker: null };
        this.pmApprovalCurrentDate = null;
        pmDate = null;
        pmBy = null;
      }
      return AccountApplicationV1.update(
        {
          id: this.accountApplication.id,
          pmApproval: { approved: pmApproved, by: pmBy, at: pmDate, type: pmType, on: pmOn },
        },
      ).$promise.then((a) => {
        this.accountApplication.pmApproval = a.pmApproval;
        this.setPMApproveDisabled();
        this.pmName = a.pmApproval.pmName;
        return this.pmName;
      }).catch((e) => {
        this.pmApprovalError = e.data;
      });
    };

    this.processComment = comment => ({
      content: comment.content,
      createdAt: comment.createdAt,
      html: comment.html,
      id: comment.id,
      userFirstName: comment.user ? comment.user.firstName : null,
      userId: comment.user ? comment.user.id : null,
      userLastName: comment.user ? comment.user.lastName : null,
    });

    this.getRegulatoryQuestions = () => {
      return {
        pep: get(this, 'accountApplication.answers.pep'),
        pedp: get(this, 'accountApplication.answers.pedp'),
        hio: get(this, 'accountApplication.answers.hio'),
        insiderPro: get(this, 'accountApplication.answers.insiderPro'),
        proFirm: get(this, 'accountApplication.answers.proFirm'),
        insider: get(this, 'accountApplication.answers.insider'),
        insiderSymbol: get(this, 'accountApplication.answers.insiderSymbol'),
        ss: get(this, 'accountApplication.answers.ss'),
        ssSymbol: get(this, 'accountApplication.answers.ssSymbol'),
      };
    };

    this.setHasRegulatoryQuestions = () => {
      const results = this.regulatoryQuestionsValues ? Object.values(this.regulatoryQuestionsValues) : [];
      if(results.some((value) => value == true)) return true;
      if(results.every((value) => value === null)) return null;
      if(results.every((value) => value === false || value === null)) return false;
    };

    this.isRegulatoryQuestionValue = (question, bool, str) => question && (question + '').toLowerCase() === str || question === bool;

    this.addComment = () => {
      if(!this.addCommentWait) {
        this.addCommentWait = true;
        return AccountApplicationV1.addComment({
          id: this.accountApplication.id,
          content: this.newComment,
        }).$promise.then((comment) => {
          this.newComment = null;
          return this.accountApplication.comments.push(this.processComment(comment));
        }).catch((e) => {
          this.newCommentError = e.data;
        }).finally(() => {
          this.addCommentWait = false;
        });
      }

      return undefined;
    };

    this.editComment = (comment) => {
      this.accountApplication.comments.forEach(c => (c.isEditing = false));
      comment.isEditing = true;
    };

    this.saveComment = (comment) => {
      if(!this.saveCommentWait) {
        this.saveCommentWait = true;
        return Comment.update(comment).$promise.then((updatedComment) => {
          _.assign(comment, this.processComment(updatedComment));
          comment.isEditing = false;
        }).catch((e) => { this.commentError = e.data; })
          .finally(() => { this.saveCommentWait = false; });
      }

      return undefined;
    };

    this.deleteComment = commentId => Comment.delete({ id: commentId })
      .$promise.then(() => this.loadComments());

    this.gotoDocuments = () => $location.path(`/clients/${this.user.id}/documents`);

    this.rebuildAgreement = (accountApplicationId, agreementId) =>
      Agreement.delete({ id: agreementId }, () =>
        Agreement.create(
          { account_application_id: accountApplicationId },
          () => location.reload(true),
        ),
      );

    this.preApplicationFundingTracking = accountApplication => accountApplication && accountApplication.submittedAt && moment(accountApplication.submittedAt) < moment('2018-06-07T12:00:00.000Z');

    this.bankAccountInfo = () => {
      const answers = get(this, 'accountApplication.answers');
      if(get(answers, 'bankCode')) {
        return `${answers.bankBranchNumber} ${answers.bankCode} ${answers.bankAccountNumber}`;
      }
      return 'No banking info provided / linked';
    };

    this.bankInitialDepositAmount = () => {
      const answers = get(this, 'accountApplication.answers');
      if(get(answers, 'depositInitialAmount')) {
        return `One Time: @ $${answers.depositInitialAmount}`;
      } else if(
        !get(answers, 'depositRecurringAmount')
        && !get(answers, 'depositInitialAmount')
      ) {
        return 'No';
      }

      return 'No initial deposit requested';
    };

    this.bankRecurringDepositFrequency = () => {
      switch(get(this, 'accountApplication.answers.depositRecurringFrequency')) {
        case '52': return 'weekly';
        case '26': return 'bi-weekly';
        case '24': return 'twice a month';
        case '12': return 'monthly';
        case '4': return 'quarterly';
        default: return undefined;
      }
    };

    this.bankRecurringDepositPlan = () => {
      const answers = get(this, 'accountApplication.answers');
      if(get(answers, 'depositRecurringAmount')) {
        return `Recurring: $${this.accountApplication.answers.depositRecurringAmount} ${this.bankRecurringDepositFrequency()}`;
      } else if(
        !get(answers, 'depositRecurringAmount')
        && !get(answers, 'depositInitialAmount')
      ) {
        // If there are no deposits of either kind, the 'initial deposit' line will say none
        // and this line will simply be omitted
        return undefined;
      }

      return 'No recurring deposit requested';
    };

    this.spousalApplication = () => {
      if(this.hasSpouse) {
        const accountsNeedingSpousalSin = ['spousal', 'spousal_rrif', 'gsrsp'];

        return accountsNeedingSpousalSin.includes(this.accountApplication.accountType);
      }

      return false;
    };

    this.formatAccountPurpose = (value) => {
      const formattedAccountPurpose = {
        retirement: 'Retirement',
        buying_a_house: 'Buying a House',
        education: 'Education',
        general_investment: 'General Investment',
        other: 'Other',
      }

      const formattedValue = formattedAccountPurpose[value];

      if(formattedValue) return formattedValue;
      return 'Not Collected';
    }

    return this;
  },
];
